/* eslint-disable ember/no-computed-properties-in-native-classes, ember/require-computed-property-dependencies */
import Model, {
  attr,
  belongsTo,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import type Task from 'linced-admin/models/cron/task';

// Proto: cron.Log.
export default class Log extends Model {
  declare [Type]: 'cron/log';

  // createTime is when the resource was created in seconds from the epoch.
  @attr('number')
  declare createTime: number | undefined;

  // startTime is the time the scheduled task begun in milliseconds from the
  // epoch - as recorded by the sending service for a more accurate time
  // estimate.
  @attr('number', { defaultValue: 0 })
  declare startTime: number;

  // finishTime is the time the scheduled task finished processing in
  // milliseconds from the epoch - as recorded by the sending service for a
  // more accurate time estimate.
  @attr('number', { defaultValue: 0 })
  declare finishTime: number;

  // taskId references the specific task this log is being created for.
  @belongsTo('cron/task', { async: true, inverse: null })
  declare taskId: AsyncBelongsTo<Task>;

  @alias('taskId')
  declare task: AsyncBelongsTo<Task>;

  // succeeded provides a simple switch of whether the task overall task ran
  // successfully.
  @attr('boolean', { defaultValue: false })
  declare succeeded: boolean;

  // level provides the logging level of any returned messages from the
  // processing of the scheduled task.
  @attr('number', { defaultValue: 0 })
  declare level: number;

  // message may contain a message returned from the service processing the
  // scheduled run task.
  @attr('string', { defaultValue: '' })
  declare message: string;

  // processing provides a boolean for tracking whether a job is in progress,
  // this will generally only be true if cron has only just sent an event,
  // waiting on the service to return a completion log. This enables tracking
  // of dead jobs.
  @attr('boolean', { defaultValue: false })
  declare processing: boolean;

  // Methods
  /**
   * took returns the amount of time it took the service to process the task in
   * milliseconds.
   */
  @computed('startTime', 'finishTime')
  get took() {
    return this.finishTime - this.startTime;
  }

  /**
   * tookToString returns the time a task took in a human understandable string.
   */
  @computed('took')
  get tookToString() {
    if (this.processing) {
      return 'currently processing...';
    }

    const taken = this.took;
    switch (true) {
      case taken / 1000 >= 1:
        return `${taken / 1000} s`;

      default:
        return `${taken} ms`;
    }
  }
}
